import { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "react-feather";
import { FaFileInvoice } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { searchEvInv } from "../../api/finance/chargeConfirmation";
import { countHeaders } from "../../api/finance/creditDebitNoteApi";
import {
  evInvDelete,
  getInvoiceById,
  invoiceSearch,
} from "../../api/finance/invoiceSearchApi";
import EvinvComponent from "../../components/finance/EvInv/EVINVComponent";
import EvInvSearch from "../../components/finance/EvInvSearch";
import InvoiceSearchComponent from "../../components/finance/InvoiceSearchComponent";
import Grid from "../../components/Grid";
import CustomAlert from "../../components/input/CustomAlert";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";
import ButtonRenderer from "../../components/renderer/ButtonRenderer";
import MultiValueSelectRenderer from "../../components/renderer/MultiValueSelectRenderer";
import {
  ActiveCellStyle,
  CurrencyRenderer,
  EVInvCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import JobMonitorReportComponent from "../../components/transaction/JobMonitor/JobMonitorReportComponent";
import { FORMS, MODULES } from "../../const";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import { invEvActions } from "../../store/finance/inv-ev/inv-ev-slice";
import { reportActions } from "../../store/report/report-slice";
import {checkSupplierPaymentHold} from "../../api/finance/supplierPaymentHoldApi";

const searchTypeConst = {
  GENERATE: "generate",
  SEARCH: "search",
};

const HEADER_FORM = FORMS.EV_INV_MANAGER.HEADER.form;

function EvInvGenerator() {
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const evInvHeaderPermissions = auth[HEADER_FORM];
  const singleInvPermission = evInvHeaderPermissions?.FORM?.SINGLE_INV;
  const singleEvPermission = evInvHeaderPermissions?.FORM?.SINGLE_EV;
  const bulkInvPermission = evInvHeaderPermissions?.FORM?.BULK_INV;
  const bulkEvPermission = evInvHeaderPermissions?.FORM?.BULK_EV;

  const pendingSearchPermission = evInvHeaderPermissions?.FORM?.PENDING_SEARCH;
  const generatedSearchPermission =
    evInvHeaderPermissions?.FORM?.GENERATED_SEARCH;
  const deleteHeaderPermission = evInvHeaderPermissions?.FORM?.DELETE;

  const gridRef = useRef();
  const searchGridRef = useRef();
  const evInvRef = useRef();

  const dispatch = useDispatch();

  const [tab, setTab] = useState(
    pendingSearchPermission ? 1 : generatedSearchPermission ? 2 : -1
  );
  const [jobs, setJobs] = useState([]);
  const [jobsEv, setJobsEv] = useState([]);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchClickedEv, setSearchClickedEv] = useState(false);
  const user = useSelector((state) => state.user);

  const [generatorSearchParams, setGeneratorSearchParams] = useState({});
  const [currentSearchType, setCurrentSearchType] = useState(null);

  const [searchParams, setSearchParams] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);

  const updateNeeded = useSelector((state) => state.invEv.updateNeeded);
  const tempCategoryId = useSelector((state) => state.invEv.categoryId);
  const isDrawerOpen = useSelector((state) => state.invEv.drawerOpen);
  const submitLoading = useSelector((state) => state.invEv.submitLoading);
  const [eihCode, setEihCode] = useState("");
  const [eihId, setEihId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteRef = useRef();
  const userCompany = useUserCompany();
  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null
  );
  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);

  const [auditInfo, setAuditInfo] = useState({});

  const handleSearch = useCallback(async (data) => {
    setSearchClicked(true);
    setIsLoading(true);
    const result = await searchEvInv(data);
    dispatch(
      invEvActions.setFromToDate({
        fromDate: data.dFromDate,
        toDate: data.dToDate,
      })
    );
    setGeneratorSearchParams(data);
    setCurrentSearchType(searchTypeConst.GENERATE);
    setJobs(
      result && result.length > 0
        ? result.map((res) => ({
            ...res,
            evCode: res.evCode ? res.evCode : "N/A",
            invCode: res.invCode ? res.invCode : "N/A",
          }))
        : []
    );
    setSelectedRows([]);
    setIsLoading(false);
  }, []);

  const handleSearchEv = useCallback(async (data) => {
    setSearchClickedEv(true);
    setIsLoading(true);
    const result = await invoiceSearch(data);
    setCurrentSearchType(searchTypeConst.SEARCH);
    setSearchParams(data);
    const evInvList = [];
    for (let jobDetail of result) {
      const formater = jobDetail.currency
        ? Intl.NumberFormat("en-US", {
            style: "currency",
            currency: jobDetail.currency,
          })
        : null;
      evInvList.push({
        ...jobDetail,
        customer:
          jobDetail.eihsingle_bulk === 1
            ? jobDetail.customer
            : jobDetail.eihtype === 1
              ? jobDetail.customer
              : "N/A",
        transporter:
          jobDetail.eihsingle_bulk === 1
            ? jobDetail.transporter
            : jobDetail.eihtype === 2
              ? jobDetail.transporter
              : "N/A",
        createdFormatDate: formatFromJsonDate(jobDetail.createddate),
        formatter: formater,
      });
    }
    setJobsEv(evInvList);
    setSelectedRows([]);
    setIsLoading(false);
  }, []);

  const buildHoldMessage = (holdResults, rows, supplierField, supplierNameField, supplierLabel) => {
    const heldJobIds = new Set(
        holdResults.filter((r) => r.sphRefType === 2).map((r) => r.sphReference)
    );
    const heldSupplierIds = new Set(
        holdResults.filter((r) => r.sphRefType === 1).map((r) => r.sphReference)
    );

    const heldJobCodes = rows
        .filter((r) => heldJobIds.has(r.jobId))
        .map((r) => r.jobCode);
    const heldSupplierNames = [
      ...new Set(
          rows
              .filter((r) => heldSupplierIds.has(r[supplierField]))
              .map((r) => r[supplierNameField])
      ),
    ];

    if (rows.length === 1) {
      const parts = [];
      if (heldJobCodes.length > 0) parts.push(`Job ${heldJobCodes[0]}`);
      if (heldSupplierNames.length > 0)
        parts.push(`${supplierLabel} ${heldSupplierNames[0]}`);
      return `${parts.join(" and ")} ${
          parts.length > 1 ? "are" : "is"
      } on payment hold. Please release before proceeding.`;
    }

    const parts = [];
    if (heldJobCodes.length > 0) parts.push(`${heldJobCodes.length} job(s)`);
    if (heldSupplierNames.length > 0)
      parts.push(`${heldSupplierNames.length} ${supplierLabel.toLowerCase()}(s)`);
    return `${parts.join(" and ")} on payment hold. Please release them before proceeding.`;
  };

  useEffect(() => {
    if (updateNeeded) {
      if (currentSearchType === searchTypeConst.GENERATE) {
        handleSearch(generatorSearchParams);
      } else {
        handleSearchEv(searchParams);
      }
      dispatch(invEvActions.setUpdateNeeded(false));
    }
  }, [
    updateNeeded,
    handleSearchEv,
    handleSearch,
    currentSearchType,
    generatorSearchParams,
    searchParams,
  ]);

  const handleDrawerClose = () => {
    dispatch(invEvActions.closeDrawer());
    dispatch(invEvActions.resetState());
  };

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const openEvInvDrawer = async (categoryId, rowData, isbulk = false) => {
    setIsValidating(true);
    if (isbulk) {
      const jobs =
        rowData.map((row) => ({ jobId: row.jobId, chgId: row.chgId })) ?? [];
      dispatch(invEvActions.setJobIds(jobs));
      dispatch(
        invEvActions.setSelectedJob(jobs.length > 0 ? jobs[0]?.chgId : null)
      );
      dispatch(
        invEvActions.setStatusUpdateRequest(
          rowData.map((row) => ({
            request: row.reqId,
            job: row.jobId,
          }))
        )
      );
    } else {
      dispatch(invEvActions.setJobId(rowData.jobId));
      dispatch(invEvActions.setChhId(rowData.chgId));
      dispatch(
        invEvActions.setStatusUpdateRequest([
          {
            request: rowData.reqId,
            job: rowData.jobId,
          },
        ])
      );
    }
    dispatch(invEvActions.setBulkMode(isbulk));
    dispatch(invEvActions.setCategoryId(categoryId));
    const evInvId = categoryId === 1 ? rowData.invId : rowData.evId;
    if (evInvId && evInvId !== -1) {
      dispatch(invEvActions.setEditMode(true));
      const invoiceData = await getInvoiceById(evInvId);
      dispatch(invEvActions.setFetchedResponse(invoiceData));
    }
    dispatch(invEvActions.setEditMode(false));
    dispatch(invEvActions.openDrawer());
    setIsValidating(false);
  };

  const actionMenuHandler = async (data) => {
    if (!selectedRows || selectedRows.length === 0) {
      toast.error("No Jobs selected!", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }
    switch (data.value) {
      case 1: {
        // Bulk INV Gen
        let customerId = null;
        for (let row of selectedRows) {
          if (customerId && row.invCusId !== customerId) {
            toast.warn(
              "Cannot generate Bulk INV for jobs that belong to multiple customers",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          } else {
            customerId = row.invCusId;
          }
          if (row.inStatus !== 3 && row.inStatus !== 6) {
            toast.warn(
              row.inStatus === 2
                ? "Cannot generate INV since there charges that are already generated!"
                : "Cannot generate INV since there are unapproved charges!",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }
        }

        const bulkInvHoldResults = await checkSupplierPaymentHold({
          evInvType: 2,
          jobIds: selectedRows.map((r) => r.jobId),
          supplierIds: [...new Set(selectedRows.map((r) => r.invCusId))],
        });
        if (bulkInvHoldResults.length > 0) {
          toast.error(
              buildHoldMessage(
                  bulkInvHoldResults,
                  selectedRows,
                  "invCusId",
                  "invCustomer",
                  "Customer"
              ),
              { autoClose: 6000, containerId: "Main" }
          );
          return;
        }
        openEvInvDrawer(1, selectedRows, true);
        break;
      }
      case 2: {
        // Bulk EV Gen
        let transporterId = null;
        for (let row of selectedRows) {
          if (transporterId && row.tranId !== transporterId) {
            toast.warn(
              "Cannot generate Bulk EV for jobs that belong to multiple transporters",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          } else {
            transporterId = row.tranId;
          }
          if (row.evStatus !== 3 && row.evStatus !== 6) {
            toast.warn(
              row.evStatus === 2
                ? "Cannot generate EV since there charges that are already generated!"
                : "Cannot generate EV since there are unapproved charges!",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }
        }

        const bulkEvHoldResults = await checkSupplierPaymentHold({
          evInvType: 1,
          jobIds: selectedRows.map((r) => r.jobId),
          supplierIds: [...new Set(selectedRows.map((r) => r.tranId))],
        });
        if (bulkEvHoldResults.length > 0) {
          toast.error(
              buildHoldMessage(
                  bulkEvHoldResults,
                  selectedRows,
                  "tranId",
                  "transporter",
                  "Transporter"
              ),
              { autoClose: 6000, containerId: "Main" }
          );
          return;
        }
        openEvInvDrawer(2, selectedRows, true);
        break;
      }
      case 3: {
        // Single INV
        if (selectedRows && selectedRows.length > 0) {
          if (selectedRows.length > 1) {
            toast.warn(
              "Cannot generate Single INV since multiple jobs are selected",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }
          const job = selectedRows[0];
          if (job.inStatus !== 3 && job.inStatus !== 6) {
            toast.warn(
              job.inStatus === 2
                ? "Cannot generate INV since there charges that are already generated!"
                : "Cannot generate INV since there are unapproved charges!",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }

          const singleInvHoldResults = await checkSupplierPaymentHold({
            evInvType: 2,
            jobIds: [job.jobId],
            supplierIds: [job.invCusId],
          });
          if (singleInvHoldResults.length > 0) {
            toast.error(
                buildHoldMessage(
                    singleInvHoldResults,
                    [job],
                    "invCusId",
                    "invCustomer",
                    "Customer"
                ),
                { autoClose: 6000, containerId: "Main" }
            );
            return;
          }

          openEvInvDrawer(1, job);
        }
        break;
      }
      case 4: {
        // Single EV
        if (selectedRows && selectedRows.length > 0) {
          if (selectedRows.length > 1) {
            toast.warn(
              "Cannot generate Single EV since multiple jobs are selected",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }
          const job = selectedRows[0];
          if (job.evStatus !== 3 && job.evStatus !== 6) {
            toast.warn(
              job.evStatus === 2
                ? "Cannot generate EV since there charges that are already generated!"
                : "Cannot generate EV since there are unapproved charges!",
              { autoClose: 5000, containerId: "Main" }
            );
            return;
          }

          const singleEvHoldResults = await checkSupplierPaymentHold({
            evInvType: 1,
            jobIds: [job.jobId],
            supplierIds: [job.tranId],
          });
          if (singleEvHoldResults.length > 0) {
            toast.error(
                buildHoldMessage(
                    singleEvHoldResults,
                    [job],
                    "tranId",
                    "transporter",
                    "Transporter"
                ),
                { autoClose: 6000, containerId: "Main" }
            );
            return;
          }
          openEvInvDrawer(2, job);
        }
        break;
      }
      default:
    }
  };

  const handleSelectionChangedEv = useCallback(() => {
    const selectedData = searchGridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const cellDoubleClickHandler = async (params) => {
    setIsFetchLoading(true);
    const invoiceById = await getInvoiceById(params.data.eihid);
    if (!invoiceById) {
      toast.error("Invalid Ev or Invoice", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }
    if (
      invoiceById?.jobEvInvoiceDetails &&
      invoiceById?.jobEvInvoiceDetails.length > 0
    ) {
      dispatch(
        invEvActions.setChhId(invoiceById?.jobEvInvoiceDetails[0].chhId)
      );
    }
    dispatch(invEvActions.setFetchedResponse(invoiceById));
    dispatch(invEvActions.setJobId(invoiceById.eihJobId));
    // dispatch(invEvActions.setChhId(rowData.chgId));
    dispatch(invEvActions.setCategoryId(invoiceById.eihType));
    dispatch(invEvActions.setEditMode(true));
    dispatch(invEvActions.setBulkMode(invoiceById.eihSingleBulk === 2));
    dispatch(invEvActions.openDrawer());
    setIsFetchLoading(false);
    setAuditInfo({
      createdDate: invoiceById.createdDate,
      createdBy: invoiceById.createdBy,
      lastModifiedDate: invoiceById.lastModifiedDate,
      lastModifiedBy: invoiceById.lastModifiedBy,
    });
  };

  const getActionMenuOptions = () => {
    const options = [];
    if (selectedRows.length === 1) {
      if (singleInvPermission) {
        options.push({
          key: 3,
          text: "Single INV",
          value: 3,
        });
      }
      if (singleEvPermission) {
        options.push({
          key: 4,
          text: "Single EV",
          value: 4,
        });
      }
    } else {
      if (bulkInvPermission) {
        options.push({
          key: 1,
          text: "Bulk INV",
          value: 1,
        });
      }
      if (bulkEvPermission) {
        options.push({
          key: 2,
          text: "Bulk EV",
          value: 2,
        });
      }
    }

    return options;
  };

  const handleDelete = async (eihId, type, auth, com) => {
    setIsDeleting(true);
    try {
      const res = await evInvDelete({
        eihId: eihId,
        type: type,
        author: auth,
        company: com,
      });

      if (res?.status === 200) {
        toast.success(`${res.data} deleted successfully`, {
          autoClose: 5000,
          containerId: "Main",
        });
        setRefresh(true);
      } else {
        toast.error(res.data, {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      setIsDeleting(false);
    } catch (e) {
      console.err(e);
    } finally {
      setIsDeleting(false);
    }
    setIsDeleting(false);
  };

  const reportModalRef = useRef();
  const [modalKey, setModalKey] = useState(1);

  const handleActionMenu = (selectedType) => {
    try {
      switch (selectedType.id) {
        case 1:
          let prevEihType = selectedRows[0].eihtype;
          let IsDifferentTypeSelect = false;
          const moduleCategory = selectedRows.map((row) => {
            if (row.eihtype === prevEihType) {
              if (row.eihtype === 1) {
                return row.eihsingle_bulk === 1 ? 2 : 4;
              } else {
                return row.eihsingle_bulk === 1 ? 3 : 5;
              }
            } else {
              IsDifferentTypeSelect = true;
              toast.error("Different EV OR INV type has selected!", {
                autoClose: 5000,
                containerId: "Modal",
              });
            }
          });
          const sanitizedRows = selectedRows.map(
            ({ formatter, ...rest }) => rest
          );
          dispatch(
            reportActions.setMultipleEvInvoiceReportData({
              evInvData: sanitizedRows,
              moduleCategory: moduleCategory[0],
              IsDifferentTypeSelect: IsDifferentTypeSelect,
            })
          );
          reportModalRef.current?.showModal();
          break;
      }
    } catch (error) {
      console.error("Error in handleChargeAdd:", error);
    }
  };

  const handleDownload = async () => {
    const rowsToDownload = selectedRows.length > 0 ? selectedRows : jobs;

    if (!rowsToDownload || rowsToDownload.length === 0) {
      toast.warn("No data to download", { autoClose: 3000 });
      return;
    }

    setIsDownloading(true);

    const headerMap = {
      chgCode: "Charge",
      requestCode: "Request",
      jobCode: "Job",
      invCustomer: "Invoicing Customer",
      customer: "Customer",
      shipperName: "Shipper",
      transporter: "Transporter",
      vehNo: "Vehicle",
      distance: "Distance",
      evStatusLabel: "EV Status",
      evCode: "EV Number",
      invStatusLabel: "INV Status",
      invCode: "INV Number",
      chgAdditionalValue: "Additional Charge",
    };

    const keys = Object.keys(headerMap);
    const headers = keys.map((k) => headerMap[k]);
    const csvRows = [headers.join(",")];

    rowsToDownload.forEach((row) => {
      const values = keys.map((k) => {
        let val = row[k] ?? "";
        val = String(val).replace(/"/g, '""');
        return `"${val}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download =
      selectedRows.length > 0
        ? "Selected_EV_INV_Jobs.csv"
        : "All_EV_INV_Jobs.csv";
    a.click();

    URL.revokeObjectURL(url);
    setIsDownloading(false);
  };

  return (
    <>
      <Modal ref={reportModalRef} closeButton={true} minHeight="500px">
        <JobMonitorReportComponent
          key={modalKey}
          evinv={true}
          downloadMultiple={true}
        />
      </Modal>
      {isDeleting && (
        <div className="blur-overlay">
          <LoadingSpinner message={`Deleting ${eihCode}...`} />
        </div>
      )}
      <CustomAlert
        ref={deleteRef}
        message={`Are you sure you want to delete ${eihCode}?`}
        onAccept={() => {
          handleDelete(eihId, 2, user.username, company);
        }}
      />

      <div className="ui tabular menu top attached">
        {!pendingSearchPermission && !generatedSearchPermission ? (
          <div
            style={{
              height: "80vh",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingSpinner
              spinner="no-permission"
              message="You dont have sufficient permissions"
            />
          </div>
        ) : (
          <>
            {pendingSearchPermission && (
              <Tab
                Icon={FaFileInvoice}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                label="Generate"
              />
            )}
            {generatedSearchPermission && (
              <Tab
                Icon={Search}
                onClick={() => {
                  setTab(2);
                }}
                active={tab === 2}
                label="Search"
              />
            )}
          </>
        )}
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <EvInvSearch
          searchResHandler={handleSearch}
          resetHandler={() => {}}
          handleDownload={handleDownload}
          isDownloading={isDownloading}
          jobs={jobs}
        />
        {searchClicked && (
          <>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                ref={gridRef}
                wrapText={false}
                heading="Pick the jobs for EV/INV generation"
                showActionMenu={selectedRows.length > 0}
                actionMenuOptions={getActionMenuOptions()}
                handleActionMenuChange={actionMenuHandler}
                multiRowSelection={true}
                onSelectionChanged={handleSelectionChanged}
                isLoading={isLoading || isValidating || submitLoading}
                searchable={true}
                config={[
                  {
                    headerName: "Charge",
                    field: "chgCode",
                    headerCheckboxSelection: true,
                    checkboxSelection: true,
                    pinned: "left",
                    minWidth: 170,
                    width: 170,
                    filter: true,
                  },
                  {
                    headerName: "Request",
                    field: "requestCode",
                    pinned: "left",
                    minWidth: 150,
                    width: 150,
                  },
                  {
                    headerName: "Job",
                    field: "jobCode",
                    pinned: "left",
                    minWidth: 150,
                    width: 150,
                  },
                  {
                    headerName: "Invoicing Customer",
                    field: "invCustomer",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Customer",
                    field: "customer",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Shipper",
                    field: "shipperName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Transporter",
                    field: "transporter",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  { headerName: "Vehicle", field: "vehNo" },
                  { headerName: "Distance", field: "distance" },
                  {
                    headerName: "EV Status",
                    field: "evStatusLabel",
                    cellRenderer: EVInvCellStyle,
                  },
                  {
                    headerName: "EV Number",
                    field: "evCode",
                  },
                  {
                    headerName: "INV Status",
                    field: "invStatusLabel",
                    cellRenderer: EVInvCellStyle,
                  },
                  {
                    headerName: "INV Number",
                    field: "invCode",
                  },
                  {
                    headerName: "Additional Charge",
                    field: "chgAdditionalValue",
                  },
                ]}
                content={jobs}
              />
            )}
          </>
        )}
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 2 ? "block" : "none" }}
      >
        <InvoiceSearchComponent
          searchResHandler={handleSearchEv}
          resetHandler={() => {}}
          refresh={refresh}
          setRefresh={setRefresh}
        />
        {searchClickedEv && (
          <>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                ref={searchGridRef}
                showDownloadButton={true}
                rowSelection="multiple"
                wrapText={false}
                multiRowSelection={true}
                showActionMenu={true}
                handleActionMenuChange={handleActionMenu}
                actionMenuOptions={[
                  {
                    id: 1,
                    value: 1,
                    text: "Reports Download",
                  },
                ]}
                onSelectionChanged={handleSelectionChangedEv}
                isLoading={isLoading || isFetchLoading || submitLoading}
                searchable={true}
                config={[
                  {
                    headerName: "EV/INV Code",
                    field: "eihcode",
                    headerCheckboxSelection: true,
                    checkboxSelection: true,
                    pinned: "left",
                    minWidth: 150,
                    width: 150,
                    filter: true,
                  },
                  {
                    headerName: "Single Or Bulk",
                    field: "eihsingle_bulk_desc",
                  },
                  { headerName: "Type name", field: "eihtype_name" },
                  { headerName: "EV Or INV", field: "ceihtype" },
                  {
                    headerName: "Job Code",
                    field: "eihjob_no",
                    cellRenderer: MultiValueSelectRenderer,
                  },
                  {
                    headerName: "Amount",
                    field: "eihwith_tax",
                    cellRenderer: CurrencyRenderer,
                    cellRendererParams: (params) => ({
                      formatter: params.data.formatter,
                    }),
                  },
                  {
                    headerName: "Amount Without Tax",
                    field: "eihwithout_tax",
                    cellRenderer: CurrencyRenderer,
                    cellRendererParams: (params) => ({
                      formatter: params.data.formatter,
                    }),
                  },
                  {
                    headerName: "Customer",
                    field: "customer",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Invoicing Customer",
                    field: "invCustomer",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Transporter",
                    field: "transporter",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Status",
                    field: "eihstatus_name",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "eihactive_namet",
                    cellRenderer: ActiveCellStyle,
                    pinned: "right",
                    width: "120",
                    minWidth: "120",
                  },
                  {
                    headerName: "Integration Status",
                    field: "eihIntStatusValue",
                  },
                  {
                    headerName: "Created Date",
                    field: "createdFormatDate",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  { headerName: "Created By", field: "createdby" },
                  {
                    field: "delete",
                    cellRenderer: ButtonRenderer,
                    pinned: "right",
                    width: "90",
                    minWidth: "90",
                    cellRendererParams: (params) => {
                      return {
                        clickHandler: async () => {
                          const res = await countHeaders(params.data.eihid);
                          if (res) {
                            toast.error(
                              "cannot delete, credit/debit notes exist for this",
                              { autoClose: 5000, containerId: "Main" }
                            );
                            return;
                          } else {
                            setEihId(params.data.eihid);
                            setEihCode(params.data.eihcode);
                            deleteRef.current?.openAlert();
                          }
                        },
                        label: "Delete",
                        Icon: MdDeleteForever,
                        className: `ui ${params.data.eihactive === 2 ? "grey" : "red"} button`,
                        disabled:
                          params.data.eihactive === 2 ||
                          !deleteHeaderPermission,
                      };
                    },
                  },
                ]}
                content={jobsEv}
                onRowDoubleClicked={cellDoubleClickHandler}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          <div className="drawer-header">
            <div style={{ padding: "10px 0 10px 0" }}>
              <h3>{tempCategoryId === 1 ? "INV" : "EV"} Generator</h3>
            </div>
          </div>
        }
        body={
          <>
            {isValidating || isFetchLoading ? (
              <LoadingSpinner />
            ) : (
              <EvinvComponent ref={evInvRef} auditInfo={auditInfo} />
            )}
          </>
        }
        style={{ width: "90vw", boxShadow: "none" }}
      />
    </>
  );
}

export default EvInvGenerator;

const styles = `
  .blur-overlay {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
    z-index: 9999; /* Ensure it's on top of everything */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .blurred {
    filter: blur(5px); /* Blur effect on content */
    pointer-events: none; /* Prevent interaction with blurred content */
  }
`;

document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);
